/**
 * SyntheticDashboard.tsx — Synthetic Monitor dashboard (/dashboard/synthetic)
 *
 * Purpose:    Manage scripted HTTP/browser checks, geo-region selector,
 *             per-region pass/fail results, and regression log.
 * Inputs:     useSyntheticData hook (port 3836)
 * Outputs:    CheckEditor + CheckResultList + RegressionLog
 * Constraints: 7-state AsyncScreen; geo-region multi-select with checkboxes
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useState } from 'react'
import { AsyncScreen } from '../../components/AsyncScreen'
import {
  useSyntheticData,
  type CheckResult,
  type RegressionEntry,
} from '../../hooks/useSyntheticData'
import { CheckEditor, REGION_LABELS } from './CheckEditor'

// ---------------------------------------------------------------------------
// Check result list
// ---------------------------------------------------------------------------

function CheckResultList({ results }: { results: CheckResult[] }) {
  if (results.length === 0)
    return <p className="text-sm text-brand-muted">No results yet.</p>

  return (
    <div className="overflow-hidden rounded-lg border border-brand-subtle">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-brand-subtle bg-brand-surface/50">
            <th className="px-4 py-2 text-left text-xs font-medium text-brand-muted">Check</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-brand-muted">Region</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-brand-muted">Result</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-brand-muted">Latency</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-brand-muted">Time</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {results.slice(0, 20).map((r) => (
            <tr key={r.id} className="hover:bg-brand-surface-2/30">
              <td className="px-4 py-2 text-xs text-brand-muted font-mono">{r.checkId.slice(0, 8)}</td>
              <td className="px-4 py-2 text-xs text-brand-secondary">{REGION_LABELS[r.region]}</td>
              <td className="px-4 py-2">
                <span className={`text-xs font-medium ${r.passed ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                  {r.passed ? 'Pass' : 'Fail'}
                </span>
                {!r.passed && r.errorMessage && (
                  <span className="ml-2 text-xs text-brand-muted">{r.errorMessage}</span>
                )}
              </td>
              <td className="px-4 py-2 text-xs tabular-nums text-brand-muted">{r.latencyMs}ms</td>
              <td className="px-4 py-2 text-xs text-brand-muted">
                {new Date(r.checkedAt).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Regression log
// ---------------------------------------------------------------------------

function RegressionLog({ regressions }: { regressions: RegressionEntry[] }) {
  if (regressions.length === 0)
    return <p className="text-sm text-brand-muted">No regressions detected.</p>

  return (
    <ul className="space-y-2">
      {regressions.map((r, i) => (
        <li key={i} className="flex items-center justify-between rounded-md border border-brand-subtle px-4 py-2 text-sm">
          <div>
            <span className="font-medium text-brand-ink">{r.checkName}</span>
            <span className="ml-2 text-xs text-brand-muted">{REGION_LABELS[r.region]}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-medium ${r.status === 'ongoing' ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
              {r.status}
            </span>
            <time className="text-xs text-brand-muted">
              {new Date(r.detectedAt).toLocaleDateString()}
            </time>
          </div>
        </li>
      ))}
    </ul>
  )
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function SyntheticSkeleton() {
  return (
    <div className="animate-pulse space-y-3 p-6" aria-hidden>
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-12 rounded-md bg-brand-surface-2" />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SyntheticDashboard() {
  const { state, data, error, refresh, saveCheck, deleteCheck } = useSyntheticData()
  const [editingId, setEditingId] = useState<string | 'new' | null>(null)

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-ink">Synthetic Monitor</h1>
          <p className="mt-1 text-sm text-brand-muted">
            Scripted HTTP and browser checks across geo-regions
          </p>
        </div>
        <button type="button" onClick={() => { void refresh() }} className="rounded-md border border-brand-card px-3 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2">
          Refresh
        </button>
      </div>

      <AsyncScreen
        state={state}
        error={error}
        onRetry={() => { void refresh() }}
        loadingSkeleton={<SyntheticSkeleton />}
        emptyMessage="No synthetic checks configured"
        emptyAction={
          <button
            type="button"
            onClick={() => setEditingId('new')}
            className="rounded-md bg-sky-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-500"
          >
            Create your first check
          </button>
        }
      >
        {data && (
          <div className="space-y-8">
            {/* Checks */}
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                  Checks ({data.checks.length})
                </h2>
                <button type="button" onClick={() => setEditingId('new')} className="text-xs text-sky-700 dark:text-sky-400 hover:underline">
                  + New check
                </button>
              </div>
              {editingId === 'new' && (
                <div className="mb-4">
                  <CheckEditor onSave={saveCheck} onCancel={() => setEditingId(null)} />
                </div>
              )}
              <div className="space-y-2">
                {data.checks.map((check) =>
                  editingId === check.id ? (
                    <CheckEditor
                      key={check.id}
                      initial={check}
                      onSave={saveCheck}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <div
                      key={check.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-brand-subtle bg-brand-surface/50 px-4 py-3"
                    >
                      <div>
                        <span className="font-medium text-brand-ink">{check.name}</span>
                        <span className="ml-2 text-xs text-brand-muted">{check.scriptType}</span>
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {check.geoRegions.map((r) => (
                            <span key={r} className="rounded bg-brand-surface-2 px-1.5 py-0.5 text-xs text-brand-muted">
                              {REGION_LABELS[r]}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`h-2 w-2 rounded-full ${check.enabled ? 'bg-green-500' : 'bg-gray-600'}`} />
                        <span className="text-xs text-brand-muted">every {check.intervalMinutes}m</span>
                        <button type="button" onClick={() => setEditingId(check.id)} className="text-xs text-sky-700 dark:text-sky-400 hover:underline">
                          Edit
                        </button>
                        <button type="button" onClick={() => { void deleteCheck(check.id) }} className="text-xs text-red-700 dark:text-red-400 hover:underline">
                          Delete
                        </button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </section>

            {/* Recent results */}
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                Recent results
              </h2>
              <CheckResultList results={data.recentResults} />
            </section>

            {/* Regressions */}
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                Regression log
              </h2>
              <RegressionLog regressions={data.regressions} />
            </section>
          </div>
        )}
      </AsyncScreen>
    </div>
  )
}
